/**
 * Offline Map Service
 * Manages offline map tiles using IndexedDB
 *
 * Architecture:
 * - Tiles stored in IndexedDB (not Supabase)
 * - Optional metadata sync with Supabase for cross-device awareness
 * - Uses idb library for IndexedDB access
 * - Supports map tile download, caching, and bundle management
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb'
import { lonToTileX, latToTileY } from './imagerySources'

// ============================================================================
// Types
// ============================================================================

export interface OfflineMapBundle {
  id: string
  userId: string // User who downloaded this bundle
  jobId: string
  jobTitle: string
  createdAt: string
  centerLat: number
  centerLng: number
  radiusMeters: number
  tileCount: number
  sizeBytes: number
  zoomLevels: number[]
  /**
   * ImagerySource id the tiles came from (feature 004). Deliberately typed
   * string, not ImagerySourceId: pre-004 bundles are backfilled with the
   * LEGACY_SOURCE_ID sentinel, which is outside the registry union.
   */
  sourceId?: string
}

/** Sentinel sourceId for tiles downloaded from the retired unauthenticated Esri endpoint. */
export const LEGACY_SOURCE_ID = 'legacy-esri'

export interface MapTile {
  key: string // Format: "sourceId/z/x/y" (legacy pre-004 tiles: "z/x/y")
  z: number
  x: number
  y: number
  blob: Blob
  bundleId: string
  downloadedAt: string
}

export interface DownloadProgress {
  bundleId: string
  totalTiles: number
  downloadedTiles: number
  failedTiles: number
  status: 'downloading' | 'completed' | 'failed' | 'cancelled'
  error?: string
}

interface OfflineMapDB extends DBSchema {
  bundles: {
    key: string
    value: OfflineMapBundle
    indexes: { 'by-job': string; 'by-user': string }
  }
  tiles: {
    key: string
    value: MapTile
    indexes: { 'by-bundle': string }
  }
}

// ============================================================================
// Database Setup
// ============================================================================

const DB_NAME = 'ArborSceneOfflineMaps'
const DB_VERSION = 3 // v2: userId index; v3: bundle.sourceId backfill (feature 004)

let dbPromise: Promise<IDBPDatabase<OfflineMapDB>> | null = null

/**
 * Initialize and open the IndexedDB database
 */
async function getDB(): Promise<IDBPDatabase<OfflineMapDB>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineMapDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        // Create bundles store
        if (!db.objectStoreNames.contains('bundles')) {
          const bundlesStore = db.createObjectStore('bundles', { keyPath: 'id' })
          bundlesStore.createIndex('by-job', 'jobId')
          bundlesStore.createIndex('by-user', 'userId')
        } else if (oldVersion < 2) {
          // Migrate existing bundles store to add userId index
          const bundlesStore = transaction.objectStore('bundles')
          if (!bundlesStore.indexNames.contains('by-user')) {
            bundlesStore.createIndex('by-user', 'userId')
          }
        }

        // Create tiles store
        if (!db.objectStoreNames.contains('tiles')) {
          const tilesStore = db.createObjectStore('tiles', { keyPath: 'key' })
          tilesStore.createIndex('by-bundle', 'bundleId')
        }

        // v3 (feature 004): backfill sourceId on pre-existing bundles so
        // legacy downloads keep rendering and are labelled correctly
        if (oldVersion > 0 && oldVersion < 3) {
          const bundlesStore = transaction.objectStore('bundles')
          void bundlesStore.openCursor().then(function backfill(cursor): Promise<void> | void {
            if (!cursor) return
            const bundle = cursor.value
            if (!bundle.sourceId) {
              void cursor.update({ ...bundle, sourceId: LEGACY_SOURCE_ID })
            }
            return cursor.continue().then(backfill)
          })
        }
      },
    })
  }
  return dbPromise
}

// ============================================================================
// Bundle Management
// ============================================================================

/**
 * Get all offline map bundles
 */
export async function getAllBundles(): Promise<OfflineMapBundle[]> {
  const db = await getDB()
  return db.getAll('bundles')
}

/**
 * Get bundles for a specific job
 */
export async function getBundlesByJobId(jobId: string): Promise<OfflineMapBundle[]> {
  const db = await getDB()
  return db.getAllFromIndex('bundles', 'by-job', jobId)
}

/**
 * Get bundles for a specific user
 */
export async function getBundlesByUserId(userId: string): Promise<OfflineMapBundle[]> {
  const db = await getDB()
  return db.getAllFromIndex('bundles', 'by-user', userId)
}

/**
 * Get a specific bundle by ID
 */
export async function getBundleById(bundleId: string): Promise<OfflineMapBundle | undefined> {
  const db = await getDB()
  return db.get('bundles', bundleId)
}

/**
 * Create a new offline map bundle
 */
export async function createBundle(
  userId: string,
  jobId: string,
  jobTitle: string,
  centerLat: number,
  centerLng: number,
  radiusMeters: number,
  zoomLevels: number[],
  sourceId: string
): Promise<OfflineMapBundle> {
  const bundle: OfflineMapBundle = {
    id: `bundle_${jobId}_${Date.now()}`,
    userId,
    jobId,
    jobTitle,
    createdAt: new Date().toISOString(),
    centerLat,
    centerLng,
    radiusMeters,
    tileCount: 0,
    sizeBytes: 0,
    zoomLevels,
    sourceId,
  }

  const db = await getDB()
  await db.add('bundles', bundle)
  return bundle
}

/**
 * Update bundle metadata (tile count, size)
 */
export async function updateBundle(bundle: OfflineMapBundle): Promise<void> {
  const db = await getDB()
  await db.put('bundles', bundle)
}

/**
 * Delete a bundle and all its tiles
 */
export async function deleteBundle(bundleId: string): Promise<void> {
  const db = await getDB()
  const tx = db.transaction(['bundles', 'tiles'], 'readwrite')

  // Delete all tiles for this bundle
  const tiles = await tx.objectStore('tiles').index('by-bundle').getAllKeys(bundleId)
  for (const key of tiles) {
    await tx.objectStore('tiles').delete(key)
  }

  // Delete the bundle
  await tx.objectStore('bundles').delete(bundleId)

  await tx.done
}

// ============================================================================
// Tile Management
// ============================================================================

/** Source-prefixed tile key; see MapTile.key for the legacy form. */
function tileKey(sourceId: string, z: number, x: number, y: number): string {
  return `${sourceId}/${z}/${x}/${y}`
}

/**
 * Get a tile from cache. Falls back to the legacy un-prefixed key so bundles
 * downloaded before feature 004 keep rendering until re-downloaded.
 */
export async function getTile(
  sourceId: string,
  z: number,
  x: number,
  y: number
): Promise<MapTile | undefined> {
  const db = await getDB()
  const tile = await db.get('tiles', tileKey(sourceId, z, x, y))
  if (tile) return tile
  return db.get('tiles', `${z}/${x}/${y}`)
}

/**
 * Save a tile to cache
 */
export async function saveTile(
  sourceId: string,
  z: number,
  x: number,
  y: number,
  blob: Blob,
  bundleId: string
): Promise<void> {
  const db = await getDB()
  const tile: MapTile = {
    key: tileKey(sourceId, z, x, y),
    z,
    x,
    y,
    blob,
    bundleId,
    downloadedAt: new Date().toISOString(),
  }
  await db.put('tiles', tile)
}

/**
 * Get all tiles for a bundle
 */
export async function getTilesByBundle(bundleId: string): Promise<MapTile[]> {
  const db = await getDB()
  return db.getAllFromIndex('tiles', 'by-bundle', bundleId)
}

/**
 * Delete a specific tile (both the source-prefixed and legacy key forms)
 */
export async function deleteTile(sourceId: string, z: number, x: number, y: number): Promise<void> {
  const db = await getDB()
  await db.delete('tiles', tileKey(sourceId, z, x, y))
  await db.delete('tiles', `${z}/${x}/${y}`)
}

// ============================================================================
// Storage Management
// ============================================================================

/**
 * Get total storage usage across all bundles
 */
export async function getStorageUsage(): Promise<{
  totalBytes: number
  bundleCount: number
  tileCount: number
}> {
  const db = await getDB()
  const bundles = await db.getAll('bundles')
  const tiles = await db.getAll('tiles')

  const totalBytes = bundles.reduce((sum, bundle) => sum + bundle.sizeBytes, 0)

  return {
    totalBytes,
    bundleCount: bundles.length,
    tileCount: tiles.length,
  }
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

// ============================================================================
// Tile Download (Placeholder)
// ============================================================================

/**
 * Calculate tile coordinates for a given area
 * Based on center point, radius, and zoom levels
 */
export function calculateTileCoordinates(
  centerLat: number,
  centerLng: number,
  radiusMeters: number,
  zoomLevels: number[]
): Array<{ z: number; x: number; y: number }> {
  const tiles: Array<{ z: number; x: number; y: number }> = []

  for (const zoom of zoomLevels) {
    // Convert lat/lng to tile coordinates at this zoom level
    const centerTile = latLngToTile(centerLat, centerLng, zoom)

    // Calculate how many tiles we need in each direction
    // Rough approximation: 1 tile at zoom 15 ≈ 4.9km × 4.9km
    const metersPerTileAtEquator = 40075017 / Math.pow(2, zoom)
    const tilesInRadius = Math.ceil(radiusMeters / metersPerTileAtEquator)

    // Generate all tiles in the square around center
    for (let dx = -tilesInRadius; dx <= tilesInRadius; dx++) {
      for (let dy = -tilesInRadius; dy <= tilesInRadius; dy++) {
        const x = centerTile.x + dx
        const y = centerTile.y + dy

        // Validate tile coordinates
        const maxTile = Math.pow(2, zoom)
        if (x >= 0 && x < maxTile && y >= 0 && y < maxTile) {
          tiles.push({ z: zoom, x, y })
        }
      }
    }
  }

  return tiles
}

/**
 * Convert lat/lng to integer tile coordinates (shared Web Mercator math)
 */
function latLngToTile(lat: number, lng: number, zoom: number): { x: number; y: number } {
  return { x: Math.floor(lonToTileX(lng, zoom)), y: Math.floor(latToTileY(lat, zoom)) }
}

/**
 * Download tiles for a bundle (stub - will be implemented in Phase 3)
 * @returns Progress callback
 */
export function downloadTilesForBundle(
  bundleId: string,
  onProgress?: (progress: DownloadProgress) => void
): void {
  // This will be fully implemented in Phase 3 when we create DownloadMapsDialog
  console.log('downloadTilesForBundle called for bundle:', bundleId)
  console.log('Full implementation in Phase 3')

  if (onProgress) {
    onProgress({
      bundleId,
      totalTiles: 0,
      downloadedTiles: 0,
      failedTiles: 0,
      status: 'completed',
    })
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Clear all offline map data (for testing/debugging)
 */
export async function clearAllData(): Promise<void> {
  const db = await getDB()
  const tx = db.transaction(['bundles', 'tiles'], 'readwrite')
  await tx.objectStore('bundles').clear()
  await tx.objectStore('tiles').clear()
  await tx.done
}

/**
 * Check if IndexedDB is available
 */
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined'
  } catch {
    return false
  }
}
